"use client";

import type { FC, HTMLAttributes, ReactNode, Ref } from "react";
import { Badge } from "@/components/ui/badge";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { Input, type InputProps } from "@/components/ui/input";
import { useStableId } from "@/lib/use-stable-id";
import { cn } from "@/lib/utils";
import {
  useValueConflictDialog,
  ValueConflictDialogContext,
} from "./value-conflict-dialog.context";

export interface ValueConflictDialogProps extends DialogProps {
  value: string;
  onValueChange: (value: string) => void;
}

const Root: FC<ValueConflictDialogProps> = ({ value, onValueChange, children, ...props }) => {
  const name = useStableId();
  return (
    <ValueConflictDialogContext.Provider
      value={{ name, selectedValue: value, select: onValueChange }}
    >
      <Dialog {...props}>{children}</Dialog>
    </ValueConflictDialogContext.Provider>
  );
};

interface HeaderProps extends HTMLAttributes<HTMLDivElement> {
  icon?: ReactNode;
  closeLabel?: string;
}

const Header: FC<HeaderProps> = ({ icon, closeLabel, className, children, ...props }) => {
  useValueConflictDialog();
  return (
    <Dialog.Header className={className} {...props}>
      {icon && (
        <span
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-bg-tint text-primary [&>svg]:h-6 [&>svg]:w-6"
        >
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
      <Dialog.Close label={closeLabel} />
    </Dialog.Header>
  );
};

interface PartProps {
  className?: string;
  children?: ReactNode;
}

const Title: FC<PartProps> = (props) => {
  useValueConflictDialog();
  return <Dialog.Title {...props} />;
};

const Description: FC<PartProps> = (props) => {
  useValueConflictDialog();
  return <Dialog.Description {...props} />;
};

interface OptionsProps extends PartProps {
  label: string;
}

const Options: FC<OptionsProps> = ({ label, className, children }) => {
  useValueConflictDialog();
  return (
    <Dialog.Body>
      <fieldset aria-label={label} className={cn("flex flex-col gap-3", className)}>
        {children}
      </fieldset>
    </Dialog.Body>
  );
};

interface ChoiceProps extends PartProps {
  value: string;
  label: ReactNode;
}

const Choice: FC<ChoiceProps> = ({ value, label, className, children }) => {
  const { name, selectedValue, select } = useValueConflictDialog();
  const checked = selectedValue === value;
  return (
    <div
      className={cn(
        // Borda `default`: sobre branco, `subtle` quase some.
        "rounded-lg border transition-colors",
        "has-[input[type=radio]:focus-visible]:outline-2 has-[input[type=radio]:focus-visible]:outline-offset-2 has-[input[type=radio]:focus-visible]:outline-link",
        checked
          ? "border-primary bg-bg-tint"
          : "border-border-default bg-surface hover:bg-bg-secondary",
        className,
      )}
    >
      <label className="flex cursor-pointer items-start gap-3 px-4 py-3.5">
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={() => select(value)}
          className="sr-only"
        />
        <span
          aria-hidden
          className={cn(
            "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 bg-surface",
            checked ? "border-primary" : "border-border-default",
          )}
        >
          {checked && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
        </span>
        {label}
      </label>
      {checked && children}
    </div>
  );
};

interface OptionProps extends PartProps {
  value: string;
  source: ReactNode;
  note?: ReactNode;
}

const Option: FC<OptionProps> = ({ value, source, note, className, children }) => (
  <Choice
    value={value}
    className={className}
    label={
      <span className="flex min-w-0 flex-col gap-1.5">
        <span className="text-body-md font-semibold tabular-nums text-fg-strong">{children}</span>
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone="outline">{source}</Badge>
          {note && <span className="text-body-xs text-fg-muted">{note}</span>}
        </span>
      </span>
    }
  />
);

interface CustomOptionProps extends PartProps {
  value: string;
  label: ReactNode;
}

const CustomOption: FC<CustomOptionProps> = ({ value, label, className, children }) => (
  <Choice
    value={value}
    className={className}
    label={<span className="text-body-md font-semibold text-fg-strong">{label}</span>}
  >
    <div className="px-4 pb-4 pl-12">{children}</div>
  </Choice>
);

interface CustomFieldProps extends Omit<InputProps, "id"> {
  label: ReactNode;
  error?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
}

const CustomField: FC<CustomFieldProps> = ({ label, error, inputRef, className, ...props }) => {
  useValueConflictDialog();
  const base = useStableId();
  const inputId = `${base}-input`;
  const errorId = `${base}-error`;
  return (
    <div className={className}>
      <label htmlFor={inputId} className="text-body-sm font-semibold text-fg-strong">
        {label}
      </label>
      <Input
        ref={inputRef}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className="mt-1.5"
        {...props}
      />
      {error && (
        <p id={errorId} className="mt-1 text-body-xs text-input-error">
          {error}
        </p>
      )}
    </div>
  );
};

const Footer: FC<HTMLAttributes<HTMLDivElement>> = (props) => {
  useValueConflictDialog();
  return <Dialog.Footer {...props} />;
};

Root.displayName = "ValueConflictDialog";
Header.displayName = "ValueConflictDialog.Header";
Title.displayName = "ValueConflictDialog.Title";
Description.displayName = "ValueConflictDialog.Description";
Options.displayName = "ValueConflictDialog.Options";
Option.displayName = "ValueConflictDialog.Option";
CustomOption.displayName = "ValueConflictDialog.CustomOption";
CustomField.displayName = "ValueConflictDialog.CustomField";
Footer.displayName = "ValueConflictDialog.Footer";

export const ValueConflictDialog = Object.assign(Root, {
  Header,
  Title,
  Description,
  Options,
  Option,
  CustomOption,
  CustomField,
  Footer,
});
